package com.iregained.site.components.sections.patients.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.patients.styles.HeaderPrimaryActionStyle
import com.iregained.site.components.sections.patients.styles.HeaderSecondaryActionStyle
import com.iregained.site.components.widgets.AnimatedHeroText
import com.iregained.site.components.widgets.SharedHeroActionLink
import com.iregained.site.components.widgets.VideoFormat
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Video
import kotlin.math.min

// UNIQUE ANIMATION: Hero Blur-Fade (Fires instantly via CSS on load)
val HeroBlurFadeKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).styleModifier { property("filter", "blur(10px)") } }
    100.percent { Modifier.opacity(1).styleModifier { property("filter", "blur(0px)") } }
}

@Composable
fun PatientsHeroSection(data: PatientsHeroSectionData = defaultPatientsHeroSectionData) {
    var showVideoModal by remember { mutableStateOf(false) }
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-header-section"

    // --- ANIMATION STATE ---
    // Combine into guaranteed pairs
    val overlines = remember(data.overline, data.rotatingOverlines) { listOf(data.overline) + data.rotatingOverlines }
    val headlines = remember(data.headline, data.rotatingHeadlines) { listOf(data.headline) + data.rotatingHeadlines }

//    var currentIndex by remember { mutableStateOf(0) }
//    var displayedHeadline by remember { mutableStateOf(headlines.firstOrNull() ?: "") }
//    var isOverlineVisible by remember { mutableStateOf(true) }
//    var showCursor by remember { mutableStateOf(true) }

    // Blinking cursor loop
//    LaunchedEffect(Unit) {
//        while (true) {
//            delay(500.milliseconds)
//            showCursor = !showCursor
//        }
//    }

    // Unified Synchronized Animation Sequence
//    LaunchedEffect(pairCount) {
//        if (pairCount <= 1) return@LaunchedEffect // Nothing to rotate
//
//        while (true) {
//            val currentHeadline = headlines[currentIndex]
//
//            // 1. Hold Phase (Show both texts)
//            delay(3500.milliseconds)
//
//            // 2. Delete Phase
//            isOverlineVisible = false // Trigger overline fade-out & slide up
//            for (i in currentHeadline.length downTo 0) {
//                displayedHeadline = currentHeadline.substring(0, i)
//
//                // OPTIMIZATION: Increased from 40ms to 60ms to prevent scroll-blocking
//                delay(60.milliseconds)
//            }
//
//            // Brief pause while both are empty
//            delay(300.milliseconds)
//
//            // 3. Increment Phase
//            currentIndex = (currentIndex + 1) % pairCount
//            val nextHeadline = headlines[currentIndex]
//
//            // 4. Reveal & Type Phase
//            isOverlineVisible = true // Trigger overline fade-in & slide down from top
//            for (i in 1..nextHeadline.length) {
//                displayedHeadline = nextHeadline.substring(0, i)
//
//                // OPTIMIZATION: Increased from 80ms to 120ms to prevent scroll-blocking
//                delay(120.milliseconds)
//            }
//        }
//    }

    if (data.headline.isNotEmpty() || data.bgVideoPath.isNotBlank()) {
        // Main Hero Container: Full screen height with an instant background image fallback
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth()
                .styleModifier { property("min-height", "100dvh") }
                .position(Position.Relative)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("background-image", "url(\"${data.bgPosterPath}\")")
                    property("background-size", "cover")
                    property("background-position", "center")
                }, contentAlignment = Alignment.Center
        ) {
            // ==========================================
            // LAYER 1: Background Looping Video
            // ==========================================
            Video(
                attrs = Modifier.fillMaxSize().objectFit(ObjectFit.Cover).position(Position.Absolute).top(0.px)
                    .left(0.px).toAttrs {
                        attr("src", data.bgVideoPath)
                        attr("poster", data.bgPosterPath)
                        attr("playsinline", "")
                        ref { htmlVideoElement ->
                            htmlVideoElement.muted = true
                            htmlVideoElement.loop = true
                            htmlVideoElement.autoplay = true
                            if (htmlVideoElement.paused) {
                                htmlVideoElement.play()
                            }
                            onDispose { }
                        }
                    })

            // ==========================================
            // LAYER 2: Darkening Overlay for Text Legibility
            // ==========================================
            Box(
                modifier = Modifier.fillMaxSize().position(Position.Absolute)
                    .backgroundColor(Color.argb(0.7f, 0f, 0f, 0f))
            )

            // ==========================================
            // LAYER 3: Main Content (Center Aligned)
            // ==========================================
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1000.px).padding(leftRight = 32.px, topBottom = 64.px)
                    .zIndex(1).animation(
                        HeroBlurFadeKeyframes.toAnimation(
                            duration = 1.5.s,
                            timingFunction = AnimationTimingFunction.EaseOut
                        )
                    ), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center
            ) {

                AnimatedHeroText(
                    headlines = headlines,
                    overlines = overlines,
                    overlineModifier = Modifier.color(Colors.White).fontSize(28.px)
                        .fontWeight(FontWeight.Bold).letterSpacing(2.px)
                        .margin(bottom = 12.px).textAlign(TextAlign.Center),
                    headlineModifier = Modifier.color(Colors.White).fontSize(clamp(2.5.cssRem, 5.vw, 4.5.cssRem))
                        .fontWeight(FontWeight.Bold).lineHeight(1.1)
                        .margin(bottom = 24.px).fillMaxWidth().textAlign(TextAlign.Center)
                )

                // DYNAMIC OVERLINE
//                SpanText(
//                    text = overlines[currentIndex].uppercase(),
//                    modifier = Modifier.color(Colors.White).fontSize(28.px).fontWeight(FontWeight.Bold)
//                        .letterSpacing(2.px).margin(bottom = 12.px).textAlign(TextAlign.Center)
//                        .styleModifier { property("transition", "opacity 0.8s ease-in-out, transform 0.8s ease-in-out") }
//                        .opacity(if (isOverlineVisible) 1 else 0)
//                        .translateY(if (isOverlineVisible) 0.px else (-15).px)
//                )

                // DYNAMIC TYPEWRITER HEADLINE
//                Div(
//                    attrs = Modifier.color(Colors.White).fontSize(clamp(2.5.cssRem, 5.vw, 4.5.cssRem))
//                        .fontWeight(FontWeight.Bold).lineHeight(1.1).margin(bottom = 24.px).fillMaxWidth()
//                        .textAlign(TextAlign.Center).toAttrs()
//                ) {
//                    Text(displayedHeadline)
//                    Span(
//                        attrs = Modifier.opacity(if (showCursor) 1 else 0).toAttrs()
//                    ) {
//                        Text("|")
//                    }
//                }

                Div(
                    attrs = Modifier.color(Colors.White).fontSize(clamp(1.25.cssRem, 1.5.vw, 1.5.cssRem))
                        .lineHeight(1.6).margin(bottom = 40.px).fillMaxWidth().textAlign(TextAlign.Center)
                        .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.subHeadline; onDispose {} } })

                Row(
                    modifier = Modifier.gap(24.px).flexWrap(FlexWrap.Wrap).margin(bottom = 32.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    if (data.primaryCtaText.isNotBlank() && data.primaryCtaPath.isNotBlank()) SharedHeroActionLink(
                        text = data.primaryCtaText,
                        path = data.primaryCtaPath,
                        styleModifier = HeaderPrimaryActionStyle.toModifier()
                    )
                    if (data.secondaryCtaText.isNotBlank() && data.secondaryCtaPath.isNotBlank()) SharedHeroActionLink(
                        text = data.secondaryCtaText,
                        path = data.secondaryCtaPath,
                        styleModifier = HeaderSecondaryActionStyle.toModifier()
                    )
                }

                // ==========================================
                // LAYER 4: Dedicated "Play Now" Trigger (Centered beneath CTAs)
                // ==========================================
                if (!data.heroVideoEmbedUrl.isNullOrBlank()) {
                    Row(
                        modifier = Modifier.cursor(Cursor.Pointer).backgroundColor(Color.argb(0.4f, 0f, 0f, 0f))
                            .border(1.px, LineStyle.Solid, Color.argb(0.3f, 255f, 255f, 255f)).borderRadius(50.px)
                            .padding(top = 8.px, bottom = 8.px, left = 8.px, right = 24.px)
                            .boxShadow(0.px, 8.px, 20.px, color = Color.argb(0.4f, 0f, 0f, 0f))
                            .onClick { showVideoModal = true },
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center
                    ) {
                        Box(
                            modifier = Modifier.size(50.px).borderRadius(50.percent).backgroundColor(Colors.White)
                                .boxShadow(0.px, 4.px, 16.px, color = Color.argb(0.5f, 0f, 0f, 0f)),
                            contentAlignment = Alignment.Center
                        ) {
                            Svg(
                                attrs = Modifier.size(18.px).margin(left = 3.px)
                                    .toAttrs { attr("viewBox", "0 0 384 512"); attr("fill", "#008080") }) {
                                Path(attrs = {
                                    attr(
                                        "d",
                                        "M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"
                                    )
                                })
                            }
                        }
                        SpanText(
                            text = data.heroPlayVideoBtnText ?: "Watch Full Video",
                            modifier = Modifier.color(Colors.White).fontSize(18.px).fontWeight(FontWeight.SemiBold)
                                .margin(left = 16.px).textShadow(0.px, 2.px, 4.px, Color.argb(0.5f, 0f, 0f, 0f))
                        )
                    }
                }
            }
        }

        // ==========================================
        // LAYER 5: High Resolution 16:9 Video Modal
        // ==========================================
        if (showVideoModal && !data.heroVideoEmbedUrl.isNullOrBlank()) {
            Box(
                modifier = Modifier.position(Position.Fixed).top(0.px).left(0.px).fillMaxSize().zIndex(1000)
                    .backgroundColor(Color.argb(0.85f, 0f, 0f, 0f)).onClick { showVideoModal = false },
                contentAlignment = Alignment.Center
            ) {
                Box(
                    modifier = Modifier.width(90.vw).maxWidth(1100.px).aspectRatio(16f / 9f).position(Position.Relative)
                        .boxShadow(0.px, 16.px, 48.px, color = Color.argb(0.5f, 0f, 0f, 0f))
                        .onClick { it.stopPropagation() }) {
                    Box(
                        modifier = Modifier.position(Position.Absolute).top((-40).px).right(0.px).cursor(Cursor.Pointer)
                            .onClick { showVideoModal = false }, contentAlignment = Alignment.Center
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            SpanText(
                                text = "Close",
                                modifier = Modifier.color(Colors.White).margin(right = 8.px).fontSize(16.px)
                            )
                            Svg(
                                attrs = Modifier.size(20.px)
                                    .toAttrs { attr("viewBox", "0 0 384 512"); attr("fill", "#FFFFFF") }) {
                                Path(attrs = {
                                    attr(
                                        "d",
                                        "M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
                                    )
                                })
                            }
                        }
                    }

                    // Embedded YouTube/Iframe Video
                    Iframe(
                        attrs = Modifier.fillMaxSize().borderRadius(8.px).toAttrs {
                            val embedUrl =
                                if (data.heroVideoEmbedUrl.contains("?")) "${data.heroVideoEmbedUrl}&autoplay=1" else "${data.heroVideoEmbedUrl}?autoplay=1"; attr(
                            "src",
                            embedUrl
                        ); attr(
                            "allow",
                            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        ); attr("allowfullscreen", "")
                        })
                }
            }
        }
    }
}

// ==========================================
// DATA MODELS & CMS IMAGE SPECS
// ==========================================
/**
 * CMS MEDIA UPLOAD SPECS FOR PATIENT EMPOWERMENT HERO:
 * * @field bgVideoPath:
 * - Aspect Ratio: 16:9 (Landscape) to cover full desktop screens natively.
 * - Recommended Dimensions: 1920x1080px (1080p).
 * - Format: WebM (Highly Preferred) or MP4 (H.264).
 * - File Size: Keep under 3MB-5MB.
 * - Content: A seamless ~15-second ambient loop of a patient engaging happily with the MyHand system.
 * * @field bgPosterPath:
 * - Aspect Ratio: 16:9 (Must exactly match the first frame of bgVideoPath).
 * - Recommended Dimensions: 1920x1080px.
 * - File Size: Under 150KB for optimal Largest Contentful Paint (LCP) performance.
 * * @field heroVideoEmbedUrl:
 * - Full-length, high-resolution explainer video hosted on YouTube (`https://www.youtube.com/embed/<ID>`).
 */
data class PatientsHeroSectionData(
    val sectionId: String? = null,
    val overline: String,
    val rotatingOverlines: List<String>,
    val headline: String,
    val rotatingHeadlines: List<String>,
    val subHeadline: String,
    val primaryCtaText: String,
    val primaryCtaPath: String,
    val secondaryCtaText: String,
    val secondaryCtaPath: String,
    val bgVideoPath: String,
    val bgPosterPath: String,
    val heroVideoEmbedUrl: String? = null,
    val heroVideoThumbnailUrl: String? = null,
    val heroPlayVideoBtnText: String? = "Watch Full Video",
    val heroVideoAriaLabel: String = "Play the MyHand<sup>®</sup> System explainer video",
    val heroVideoFormat: VideoFormat = VideoFormat.SQUARE_1_1
) {
    init {
        require(rotatingOverlines.isNotEmpty()) { "rotatingOverlines must not be empty" }
        require(rotatingHeadlines.isNotEmpty()) { "rotatingHeadlines must not be empty" }
    }
}

val defaultPatientsHeroSectionData = PatientsHeroSectionData(
    sectionId = "patients-header-section",
    // EXACT PAIR 1
    overline = "RECOVER",
    headline = "YOUR HAND FUNCTION",
    // REMAINING PAIRS FOR ROTATION
    rotatingOverlines = listOf("RECLAIM", "RESTORE"),
    rotatingHeadlines = listOf("YOUR INDEPENDENCE", "YOUR QUALITY OF LIFE"),

    subHeadline = "Take charge of your hand recovery with the MyHand<sup>®</sup> System, an evidence-based rehabilitation technology designed specifically for stroke & brain survivors.",
    primaryCtaText = "Request a Demo",
    primaryCtaPath = "/contact",
    secondaryCtaText = "Recovery Stories",
    secondaryCtaPath = "/testimonials#patients-recovery-stories",
    bgVideoPath = Res.Videos.PatientsPage.PATIENTS_VIDEO,
    bgPosterPath = Res.Images.PatientsPage.OVERVIEW_BG,
    heroVideoEmbedUrl = Res.Strings.Links.MYHAND_HOW_IT_WORKS,
)
